import type { Question } from "../../types.js";

type Seed = Omit<Question, "rating" | "conceptId"> & { rating: number };

const raw: readonly Seed[] = [
  {
    id: "na00",
    rating: 1000,
    text: "What makes notanyai “adaptive”?",
    correctIndex: 1,
    options: [
      "It shows every question in a fixed order",
      "It picks the next question from your Elo, a curriculum graph, and a per-concept mastery model",
      "It only repeats questions on a simple timer",
      "It randomly shuffles one fixed quiz",
    ],
  },
  {
    id: "na01",
    rating: 1150,
    text: "When you answer one question correctly, notanyai’s mastery model…",
    correctIndex: 2,
    options: [
      "Forgets the result after the session ends",
      "Only updates that one question",
      "Raises confidence in the prerequisite concepts it builds on, too",
      "Resets your rating to the starting value",
    ],
  },
  {
    id: "na02",
    rating: 1300,
    text: "Which graph algorithms help notanyai decide what is most important to teach next?",
    correctIndex: 0,
    options: [
      "PageRank, HITS, betweenness, and topological order over the curriculum graph",
      "A single random number generator",
      "Alphabetical sorting of concept names",
      "The order concepts were added to the database",
    ],
  },
  {
    id: "na03",
    rating: 1050,
    text: "What does notanyai give you that a one-off training video never can?",
    correctIndex: 3,
    options: [
      "A longer runtime",
      "Background music",
      "A completion certificate with no testing",
      "Auditable, per-concept evidence of who has mastered what",
    ],
  },
  {
    id: "na04",
    rating: 1300,
    text: "For an audit of staff AI competence, notanyai can show…",
    correctIndex: 0,
    options: [
      "Per-person, per-concept mastery as a query",
      "Only a count of videos watched",
      "Nothing — learning is not measured",
      "A single org-wide pass/fail with no detail",
    ],
  },
  {
    id: "na05",
    rating: 1000,
    text: "Besides AI literacy, what can notanyai teach?",
    correctIndex: 2,
    options: [
      "Only languages",
      "Only first aid",
      "Any subject — companies build their own pool of trainings and tests",
      "Nothing else; it is a single fixed course",
    ],
  },
  {
    id: "na06",
    rating: 1200,
    text: "How does a company add its own training to notanyai?",
    correctIndex: 1,
    options: [
      "By rewriting the engine for each topic",
      "By authoring a SubjectPack — content is fully separate from the engine",
      "By emailing the vendor a PDF to host",
      "It cannot; only AI literacy ships",
    ],
  },
  {
    id: "na07",
    rating: 1250,
    text: "“One engine, many SubjectPacks” means a company can run…",
    correctIndex: 0,
    options: [
      "Onboarding, security, compliance, and product training in one place",
      "Exactly one course, forever",
      "Only consumer language lessons",
      "A spreadsheet of quiz scores",
    ],
  },
  {
    id: "na08",
    rating: 1050,
    text: "When notanyai is self-hosted, where does your learners’ data live?",
    correctIndex: 3,
    options: [
      "In a third-party SaaS vendor’s cloud",
      "In Google’s cloud",
      "On the public internet",
      "Inside your own cloud, VPC, or on-prem",
    ],
  },
  {
    id: "na09",
    rating: 1200,
    text: "Why does self-hosting matter for regulated industries?",
    correctIndex: 0,
    options: [
      "Data sovereignty — sensitive content never leaves your perimeter",
      "It makes the app load images faster",
      "It removes the need for any authentication",
      "It lets anyone read other learners’ answers",
    ],
  },
  {
    id: "na10",
    rating: 1300,
    text: "The notanyai stack you self-host is…",
    correctIndex: 1,
    options: [
      "A closed binary you cannot inspect",
      "A static front-end bundle plus an open Supabase backend",
      "A mainframe appliance shipped by post",
      "A per-seat license server only",
    ],
  },
  {
    id: "na11",
    rating: 1100,
    text: "Which is a privacy-by-design feature of notanyai?",
    correctIndex: 2,
    options: [
      "Selling learner data to advertisers",
      "Storing passwords in plain text",
      "Row-level security, anonymized retention, and self-service deletion",
      "Requiring a social-media login",
    ],
  },
  {
    id: "na12",
    rating: 1250,
    text: "When a user deletes their account, their practice history is…",
    correctIndex: 0,
    options: [
      "Anonymized for calibration, with no link back to them",
      "Emailed to the whole company",
      "Kept fully identifiable forever",
      "Sold to a data broker",
    ],
  },
  {
    id: "na13",
    rating: 1000,
    text: "What keeps learners coming back to notanyai?",
    correctIndex: 1,
    options: [
      "Pop-up ads between questions",
      "Daily streaks, mini-games, quests, and GameMaster styles",
      "Mandatory weekly meetings",
      "A single long PDF to re-read",
    ],
  },
  {
    id: "na14",
    rating: 1200,
    text: "A “GameMaster style” lets a learner pick…",
    correctIndex: 3,
    options: [
      "The font size only",
      "Which ads they see",
      "A different vendor",
      "A patient coach, a drill sergeant, or a balanced default",
    ],
  },
  {
    id: "na15",
    rating: 1100,
    text: "What does “no lock-in” mean for notanyai?",
    correctIndex: 0,
    options: [
      "No proprietary content format and no mandatory hosted dependency",
      "You must renew a license every month or lose access",
      "Content only opens in one vendor’s viewer",
      "Exporting your data costs extra",
    ],
  },
  {
    id: "na16",
    rating: 1300,
    text: "Roughly what does a notanyai production deployment run on?",
    correctIndex: 2,
    options: [
      "A rack of dedicated GPU servers",
      "A six-figure annual cloud contract",
      "A single small ARM VM (about €4/month)",
      "A personal laptop that must stay on",
    ],
  },
];

const conceptById: Readonly<Record<string, string>> = {
  na00: "adaptive_core",
  na01: "adaptive_core",
  na02: "adaptive_core",
  na03: "measured_proof",
  na04: "measured_proof",
  na05: "your_content",
  na06: "your_content",
  na07: "your_content",
  na08: "self_hosted",
  na09: "self_hosted",
  na10: "self_hosted",
  na11: "privacy_first",
  na12: "privacy_first",
  na13: "engagement_loop",
  na14: "engagement_loop",
  na15: "open_no_lockin",
  na16: "open_no_lockin",
};

function conceptForId(id: string): string {
  return conceptById[id] ?? "adaptive_core";
}

export const NOTANY_SEED_QUESTIONS: readonly Question[] = raw.map(
  (q): Question => ({
    id: q.id,
    conceptId: conceptForId(q.id),
    rating: q.rating,
    text: q.text,
    correctIndex: q.correctIndex,
    options: q.options as [string, string, string, string],
    source: "seed",
  })
);
